import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from 'api/api';
import toast from 'app/toast';
import useBundleTranslation from 'i18n';
import { bootstrapNounKey, isBootstrapCard } from 'app/extreport/concierge/answering/bootstrap';
import { subscribeKgPublicationChanged } from 'components/common/concierge-kg-model-cards/kgPublicationChannel';
import {
    IKgCardListResponse,
    IKgModelCard,
    IKgTransitionResponse,
} from 'app/extreport/concierge/answering/kgModelCardTypes';

const POLL_MS = 5000;

// Every consumer observes the same cache entry and sees the same profiling-to-settled flip, so a
// run failure is announced once per run_uid for the whole page, not once per mounted consumer.
const announcedFailures = new Set<string>();

const isRunning = (card: IKgModelCard): boolean =>
    card.registration_status === 'profiling' || card.active_run_uid !== null;

export function useConciergeKgCards({
    entityId,
    actionUrl,
    initial,
    modelCount,
}: {
    entityId: number;
    actionUrl: string;
    initial: IKgModelCard[];
    modelCount?: number;
}) {
    const { t } = useBundleTranslation(['components/common/concierge-answering']);
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => ['conciergeKgCards', entityId], [entityId]);

    const { data } = useQuery<IKgCardListResponse>({
        queryKey,
        queryFn: async () => {
            const response = await instance.get<{ status: string; data: IKgCardListResponse }>(actionUrl);
            return response.data.data;
        },
        initialData: { concierge_kg_models: initial },
        // The server list is only re-read by the poll and the publication signal, never on mount or focus.
        staleTime: Infinity,
        // The entry must not outlive its consumers: a remount has to take the fresh server-rendered list.
        gcTime: 0,
        refetchInterval: (query) => (query.state.data?.concierge_kg_models.some(isRunning) ? POLL_MS : false),
        refetchIntervalInBackground: true,
    });
    const cards = data.concierge_kg_models;

    const prevCardsRef = useRef<IKgModelCard[]>(cards);
    useEffect(() => {
        const prev = prevCardsRef.current;
        prevCardsRef.current = cards;
        cards.forEach((card) => {
            const before = prev.find((p) => p.identity_id === card.identity_id);
            const runUid = card.last_run_failure?.run_uid;
            if (
                before?.registration_status !== 'profiling' ||
                card.registration_status === 'profiling' ||
                !runUid ||
                runUid === before.last_run_failure?.run_uid ||
                announcedFailures.has(runUid)
            ) {
                return;
            }
            announcedFailures.add(runUid);
            toast(
                'error',
                isBootstrapCard(card) ? t('toast_discovery_failed') : t('toast_run_failed', { model: card.name }),
            );
        });
    }, [cards]);

    // cancelRefetch:false joins a read already in flight, so N consumers signalled together make one GET.
    useEffect(
        () =>
            subscribeKgPublicationChanged((modelUid) => {
                const current = queryClient.getQueryData<IKgCardListResponse>(queryKey);
                if (!current?.concierge_kg_models.some((card) => card.model_object_uid === modelUid)) return;
                queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
            }),
        [queryClient, queryKey],
    );

    // A bootstrap card keeps the server count; a batch of registered cards has no known count yet.
    const discoveryNounCount = (card: IKgModelCard): number =>
        isBootstrapCard(card) ? (modelCount ?? cards.length) : 0;

    const post = (card: IKgModelCard) =>
        instance.post<{ status: string; data: IKgTransitionResponse }>(`${actionUrl}/${card.identity_id}/add`);

    const mutation = useMutation({
        mutationFn: async ({ targets }: { targets: IKgModelCard[]; batch: boolean }) => {
            let last: Awaited<ReturnType<typeof post>> | undefined;
            for (const card of targets) {
                last = await post(card);
            }
            return last!;
        },
        // A poll in flight would land after the response and replay the pre-click list over it.
        onMutate: () => queryClient.cancelQueries({ queryKey }),
        onSuccess: async (response, { targets, batch }) => {
            // A poll or signal read started during the POST must not land after this write.
            await queryClient.cancelQueries({ queryKey });
            const card = targets[0];
            if (batch) {
                toast(
                    'success',
                    t('toast_discovery_started', {
                        nounA: t(bootstrapNounKey(card.bi_tool, discoveryNounCount(card))),
                    }),
                );
            } else {
                toast(
                    'success',
                    card.incremental ? t('toast_add_incremental') : t('toast_add_first', { model: card.name }),
                );
            }
            queryClient.setQueryData<IKgCardListResponse>(queryKey, {
                concierge_kg_models: response.data.data.concierge_kg_models,
            });
        },
        onError: (error: any) => {
            toast('error', error?.response?.data?.data?.message ?? t('action_failed'));
            // A batch can fail after earlier adds landed; re-read so those rows are not left as not_added.
            return queryClient.invalidateQueries({ queryKey });
        },
    });

    const addAll = () => {
        const targets = cards.filter(
            (card) => isBootstrapCard(card) || (!card.source_removed && card.registration_status === 'not_added'),
        );
        if (targets.length) mutation.mutate({ targets, batch: true });
    };

    return {
        cards,
        addAll,
        addModel: (card: IKgModelCard) => mutation.mutate({ targets: [card], batch: isBootstrapCard(card) }),
        adding: mutation.isPending,
        addingIdentityId:
            mutation.isPending && !mutation.variables.batch ? mutation.variables.targets[0].identity_id : null,
    };
}
